// /odabir-nacina-placanja — original (pre-redesign) checkout. A/B baseline (DEV-4731).
// The v2.5 redesign is a separate page at /dopunsko-zdravstveno-osiguranje-b/odabir-nacina-placanja (checkoutB.page.tsx).
import styled from '@emotion/styled';
import { IconArrowLeft } from "@kompare/kompare-ui-library";
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFunnelNavigate } from 'src/common/hooks/useFunnelNavigate.hook';
import aircash from 'src/assets/images/Aircash.png';
import keks from 'src/assets/images/keks.png';
import {
  formatNumber,
  i18n,
  openCallMeModal,
  processQueryResult,
  resetPersonalVoucher,
  RootState,
  setOfferId,
  setPersonalVoucher,
  setProgressBarPercentage,
  useAppDispatch,
  useAppSelector,
} from 'src/common';
import { CheckoutHealthOfferDto, useCheckoutHealthOfferPaymentOptionsQuery } from 'src/common/api/apollo/supplementary';
import { setOverflowActive } from 'src/common/redux/state/overflow.slice';
import {
  Accordion,
  ArrowRightIcon,
  Button,
  Card,
  CardIcon,
  Flex,
  GeneralTranferPaymentInfoCard,
  Installments,
  Loader,
  RequestAgentCard,
  SlipIcon,
  Text,
  VoucherCodeCard,
  VoucherInfoCard,
  WsPayIframe,
  HealthOfferPaymentOptionEnum
} from 'src/components';
import config from 'src/config';
import { colors } from 'src/theme';
import { AppRoutes } from '../../_router/app.routes';

const LogoStyle = styled.img({
  height: '30px',
});

const ButtonContainerStyle = styled(Flex)({
  marginTop: 20,
  marginBottom: 20,
});

const CardStyle = styled(Card)({
  padding: '8px 16px',
  textAlign: 'center',
});

const TextStyle = styled(Text)({
  textAlign: 'center',
});

// Provjera je li voucher validan
const hasValidVoucher = (modifier: any) => {
  if (!modifier) return false;
  if (typeof modifier.amount !== 'number') return false;
  if (modifier.amount === 0) return false;
  if (!modifier.type) return false;
  if (!['fixed', 'percentage'].includes(modifier.type)) return false;
  if (modifier.type === 'percentage' && (modifier.amount < 0 || modifier.amount > 100)) return false;
  return true;
};

const CheckoutPage = () => {
  const dispatch = useAppDispatch();
  const params = useParams<{ offerId: string }>();
  const navigate = useFunnelNavigate();

  const offerId = params.offerId as string;

  const [openPaymentMethodIndex, setOpenPaymentMethodIndex] = useState<number | null>(null);
  const [iframeLoadingStates, setIframeLoadingStates] = useState<{ [key: string]: boolean }>({});

  const overflowState = useAppSelector((store: RootState) => store.state.overflow);
  const soId = useAppSelector((store: RootState) => store.model.offer.soIdWeb);
  const personalVoucherState = useAppSelector((store: RootState) => store.state.personalVoucher) || {};
  useEffect(() => {
    dispatch(setProgressBarPercentage(80));
  }, []);

  useEffect(() => {
    const twelveAndHalfMinutesMs = (12 * 60 + 30) * 1000;
    const timeoutId = window.setTimeout(() => {
      window.location.reload();
    }, twelveAndHalfMinutesMs);
    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const success = queryParams.get('Success');
    const responseCode = queryParams.get('ResponseCode');

    if (success && success === '0') {
      navigate(`${AppRoutes.CHECKOUT_FAILURE.fullPath}?ErrorCodes=${responseCode ?? success}`);
    }
  }, []);

  useEffect(() => {
    dispatch(setOfferId(offerId));
  }, [offerId]);

  useEffect(() => {
    // 1) Označi trenutni zapis kao "checkout" bez stvaranja novog history entry-a
    try {
      window.history.replaceState(
        { ...(window.history.state || {}), __isCheckout: true },
        document.title,
        window.location.href
      );
    } catch (e) {
      console.error('Error replacing state:', e);
    }

    // 2) Prereži sve "forward" unose dodavanjem current checkouta na kraj
    try {
      window.history.pushState(
        { ...(window.history.state || {}), __isCheckout: true },
        document.title,
        window.location.href
      );
    } catch (e) {
      console.error('Error pushing state:', e);
    }

    // 3) Ako korisnik stisne Back i dođe na stariji "checkout" zapis, preskoči ga do prave prethodne stranice
    const onPopState = () => {
      if (window.history.state && window.history.state.__isCheckout) {
        window.history.go(-1);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const { loading, data } = useCheckoutHealthOfferPaymentOptionsQuery({
    variables: { offerId, soId: soId ?? '' },
    fetchPolicy: 'cache-first',
    onCompleted: (result: any) => {
      processQueryResult(result);

      if (result.overflowVoucherStatus) {
        dispatch(setOverflowActive({ active: result.overflowVoucherStatus.active ?? false, voucherCode: result.overflowVoucherStatus.voucherCode ?? '', voucherValue: result.overflowVoucherStatus.voucherValue ?? 0, voucherDescription: result.overflowVoucherStatus.voucherDescription ?? '' }));
      } else {
        dispatch(setOverflowActive({ active: false, voucherCode: '', voucherValue: 0, voucherDescription: '' }));
      }
      if (result.personalVoucherStatus) {
        dispatch(setPersonalVoucher(result.personalVoucherStatus));
      } else {
        dispatch(resetPersonalVoucher());
      }
    },
  });

  // Destrukturiranje podataka
  const { checkoutHealthOfferPaymentOptions } = data || {};
  const checkoutHealthOffer = checkoutHealthOfferPaymentOptions?.offer as CheckoutHealthOfferDto;

  const handleIframeLoad = (iframeKey: string) => {
    setIframeLoadingStates(prev => ({
      ...prev,
      [iframeKey]: false
    }));
  };

  const paymentOptions = useMemo(() => {
    if (!checkoutHealthOfferPaymentOptions?.paymentOptions) return [];
    return [
      {
        key: 'creditCard',
        type: 'oneTime',
        title: i18n.t('pages.checkoutPage.onetimeCard'),
        icon: <CardIcon color={colors.icon1} width={24} height={24} />,
        lazy: false,
        content: (
          <WsPayIframe key='creditCard' offerId={offerId!} paymentOption={HealthOfferPaymentOptionEnum.CreditCard} paymentType="visa" onLoad={() => handleIframeLoad('creditCard')} />
        ),
      },
      {
        key: 'creditCardWithInstallments',
        type: 'installments',
        title: i18n.t('pages.checkoutPage.installmentsCard'),
        icon: <CardIcon color={colors.icon1} width={24} height={24} />,
        lazy: false,
        content: (
          <Flex vertical gap={24}>
            <Flex gap={20}>
              <ArrowRightIcon color={colors.icon1} />
              <Installments data={checkoutHealthOfferPaymentOptions}></Installments>
            </Flex>
            <CardStyle radius={8} background={colors.primary5}>
              <Text type="small">{i18n.t('pages.checkoutPage.installmentsCardDescription')}</Text>
            </CardStyle>
            <WsPayIframe
              key="creditCardWithInstallments"
              offerId={offerId!}
              paymentOption={HealthOfferPaymentOptionEnum.CreditCard}
              paymentType="visa"
              installments
              onLoad={() => handleIframeLoad('creditCardWithInstallments')}
            />
          </Flex>
        ),
      },
      {
        key: 'generalTransfer',
        type: 'oneTime',
        title: i18n.t('pages.checkoutPage.onetimeAccount'),
        icon: <SlipIcon color={colors.icon1} width={24} height={24} />,
        lazy: true,
        content: <GeneralTranferPaymentInfoCard type="oneTime" offerId={offerId} />,
      },
      {
        key: 'generalTransferWithInstallments',
        type: 'installments',
        title: i18n.t('pages.checkoutPage.installmentsAccount'),
        icon: <SlipIcon color={colors.icon1} width={24} height={24} />,
        lazy: true,
        content: <GeneralTranferPaymentInfoCard type="installments" offerId={offerId} />,
      },
      {
        key: 'keksPay',
        type: 'oneTime',
        title: i18n.t('pages.checkoutPage.keksPay'),
        icon: <LogoStyle src={keks} alt="" />,
        lazy: true,
        content:
          config.REACT_APP_IS_DEV === 'true' ? (
            'content'
          ) : (
            <WsPayIframe
              key="keksPay"
              offerId={offerId}
              paymentOption={HealthOfferPaymentOptionEnum.KeksPay}
              paymentType="keksPay"
              onLoad={() => handleIframeLoad('keksPay')}
            />
          ),
      },
      {
        key: 'aircash',
        type: 'oneTime',
        title: i18n.t('pages.checkoutPage.aircash'),
        icon: <LogoStyle src={aircash} alt="" />,
        lazy: true,
        content:
          config.REACT_APP_IS_DEV === 'true' ? (
            'content'
          ) : (
            <WsPayIframe
              key="aircash"
              offerId={offerId}
              paymentOption={HealthOfferPaymentOptionEnum.Aircash}
              paymentType="aircash"
              onLoad={() => handleIframeLoad('aircash')}
            />
          ),
      },
    ]
      .filter((pm) => {
        // Prvo provjerimo postoji li opcija plaćanja u paymentOptions
        const hasPaymentOption =
          checkoutHealthOfferPaymentOptions!.paymentOptions!.find((po: any) => po.paymentOption === pm.key)!
            .paymentTypes!.length > 0;

        // Ako postoji validan voucher, filtriraj samo jednokratne opcije plaćanja
        if (hasValidVoucher(checkoutHealthOffer?.supplementary?.voucherPriceChangeModifier)) {
          return hasPaymentOption && pm.type === 'oneTime';
        }

        return hasPaymentOption;
      })
      .map((pm) => {
        // Pronađi odgovarajuću payment opciju iz podataka
        const paymentOptionData = checkoutHealthOfferPaymentOptions?.paymentOptions?.find(
          (po: any) => po.paymentOption === pm.key
        );

        // Uzmi prvi payment type i prvi installment
        const firstPaymentType = paymentOptionData?.paymentTypes?.[0];
        const firstInstallment = firstPaymentType?.installments?.[0];

        // Dodaj amount i discount u payment option
        return {
          ...pm,
          amount: firstInstallment?.amount,
          paymentTypeDiscount: firstInstallment?.paymentTypeDiscount || 0,
        };
      });
  }, [offerId, checkoutHealthOfferPaymentOptions, checkoutHealthOffer?.supplementary?.voucherPriceChangeModifier, checkoutHealthOffer?.brandId]);

  if (!offerId) return null;
  const isAnyIframeLoading = Object.values(iframeLoadingStates).some(iframeLoading => iframeLoading);
  if (loading || isAnyIframeLoading) return <Loader page />;

  if (!checkoutHealthOfferPaymentOptions) return null;

  const handleOpenPaymentMethod = (index: number) => {
    setOpenPaymentMethodIndex(openPaymentMethodIndex === index ? null : index);
  };

  const handleBackButton = () => {
    navigate(`${AppRoutes.OFFER.fullPath}/${offerId}`);
  };

  return (
    <Flex vertical gap={12}>
      <VoucherInfoCard active={overflowState.overflowActive} voucherCode={overflowState.voucherCode} text={overflowState.voucherDescription} />
      {!overflowState.overflowActive && personalVoucherState.personalVoucher?.status === 'Active' && personalVoucherState.personalVoucher?.value && personalVoucherState.personalVoucher.value > 0 && (
        <VoucherInfoCard active={true} voucherCode={personalVoucherState.personalVoucher?.code ?? ''} text={personalVoucherState.personalVoucher?.description ?? ''}
          showCode={true}
          endDate={personalVoucherState.personalVoucher?.endDate ?? ''}
        />
      )}
      <Flex vertical gap={24}>
        <Text type="xxxLarge" bold>
          {i18n.t('pages.checkoutPage.paymentMethod')}
        </Text>
        <VoucherCodeCard offerId={offerId} appliedVoucherCode={checkoutHealthOffer?.supplementary?.voucherPriceChangeModifier?.code} />
        <Flex vertical gap={8}>
          {paymentOptions.map((pm, index) => (
            <Card padding={16} radius={12} key={index}>
              <Accordion
                key={index}
                title={
                  <Flex gap={16} align="center" justify="space-between">
                    <Flex gap={12} align="center">
                      {pm.icon}
                      <Text type="base">{pm.title}</Text>
                    </Flex>
                    <Flex vertical align="flex-end">
                      {pm.amount && (
                        <Text type="small" bold color={colors.text1}>
                          {pm.key === 'generalTransferWithInstallments' ? `${formatNumber(pm.amount / 12)}€/mj` : `${formatNumber(pm.amount)}€`}
                        </Text>
                      )}
                      {pm.key === 'generalTransferWithInstallments' && pm.amount && (
                        <Text type="tiny" color={colors.text4}>
                          {`${formatNumber(pm.amount)}€`}
                        </Text>
                      )}
                      {/* {!!pm.paymentTypeDiscount && pm.paymentTypeDiscount > 0 && (
                      <Text type="tiny" color={colors.text4}>
                        {`Uključeno ${pm.paymentTypeDiscount}% popusta`}
                      </Text>
                    )} */}
                    </Flex>
                  </Flex>
                }
                content={pm.content}
                open={openPaymentMethodIndex === index}
                expandable={true}
                expandIcon={false}
                lazy={pm.lazy}
                // expandIconComponent={<ArrowDownIcon color={colors.icon1} />}
                gap={20}
                onChange={() => handleOpenPaymentMethod(index)}
              />
            </Card>
          ))}
          <TextStyle type="tiny" color={colors.text4}>
            {i18n.t('pages.checkoutPage.paymentCardDescription')}
          </TextStyle>
        </Flex>
        <ButtonContainerStyle justify="center" align="center">
          <Button
            btnType="textBlack"
            iconPosition="left"
            text={i18n.t('pages.offerPage.backButton')}
            onClick={handleBackButton}
          >
            {<IconArrowLeft />}
          </Button>
        </ButtonContainerStyle>
        <RequestAgentCard onButtonClick={() => dispatch(openCallMeModal())} />
      </Flex>
    </Flex>
  );
};

export default CheckoutPage;
